/**
 * The flow graph: time across, each selected metric up, one colour per metric.
 *
 * Songs are not attached to it. The curves are targets; the engine's answer is
 * drawn under them as flat steps (each song's measured value for the metric
 * being edited, across the seconds it has the air), so it is plain where the
 * library could not reach the curve.
 *
 * Editing the metric in front: press on empty graph to add an anchor there (and
 * keep dragging it), drag an anchor to move it, double-click one to remove it.
 * The other metrics' curves stay visible but faint. A repeating flow shows one
 * cycle, with the playhead wrapping; past the end of an endless flow the curves
 * hold and the playhead waits at the right edge.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  addPoint,
  clampPoint,
  clock,
  curveMoment,
  curveRuns,
  sectionBounds,
  sectionValueAt,
  METRICS,
  movePoint,
  removePoint,
  type CurvePoint,
  type FlowPick,
  type FlowSpec,
  type Metric,
} from "./flowMath";

const HEIGHT = 250;
const PAD = { left: 40, right: 28, top: 14, bottom: 22 };

export function FlowCanvas({
  spec,
  active,
  picks,
  position,
  onCurve,
  onSeek,
}: {
  spec: FlowSpec;
  active: Metric;
  picks: FlowPick[];
  position: number | null;
  onCurve: (metric: Metric, points: CurvePoint[]) => void;
  onSeek: (t: number) => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const drag = useRef<{ index: number; points: CurvePoint[] } | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  /** The last press on an anchor, to tell a double-click (remove) from a press to drag. */
  const lastPress = useRef<{ index: number; at: number } | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(Math.max(300, el.clientWidth)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const L = spec.length;
  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + (Math.min(L, Math.max(0, t)) / L) * plotW;
  const tOf = (px: number) => ((px - PAD.left) / plotW) * L;
  const y = (m: Metric, v: number) => {
    const info = METRICS[m];
    return PAD.top + (1 - (v - info.min) / (info.max - info.min)) * plotH;
  };
  const vOf = (m: Metric, py: number) => {
    const info = METRICS[m];
    return info.min + (1 - (py - PAD.top) / plotH) * (info.max - info.min);
  };

  const local = (e: { clientX: number; clientY: number }) => {
    const r = svg.current!.getBoundingClientRect();
    return { px: e.clientX - r.left, py: e.clientY - r.top };
  };

  const points = spec.curves[active] ?? [];

  const startDrag = (e: ReactPointerEvent, index: number, list: CurvePoint[]) => {
    e.preventDefault();
    e.stopPropagation();
    svg.current?.setPointerCapture(e.pointerId);
    drag.current = { index, points: list };
  };

  const onMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    const { px, py } = local(e);
    const next = movePoint(d.points, d.index, clampPoint(active, L, { t: tOf(px), v: vOf(active, py) }));
    d.points = next;
    onCurve(active, next);
  };

  const endDrag = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Which part of the timeline the picks shown belong to.
  const cycle = spec.mode === "repeat" && position !== null ? Math.floor(position / L) : 0;
  const shown = picks.filter((p) => spec.mode !== "repeat" || Math.floor((p.start + 0.5) / L) === cycle);
  const offset = cycle * L;
  const playX = position === null ? null : x(curveMoment(spec, position));
  const pastEnd = spec.mode === "endless" && position !== null && position > L;

  const gridMinutes = L <= 1800 ? 5 : L <= 3600 ? 10 : L <= 7200 ? 15 : 30;
  const ticks: number[] = [];
  for (let t = 0; t <= L + 1; t += gridMinutes * 60) ticks.push(t);

  // One run per section: the line breaks at each border (each section is its own curve).
  const pathFor = (m: Metric) =>
    curveRuns(spec, m).map((run) => run.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(m, p.v).toFixed(1)}`).join(" ")).join(" ");

  // The BPM target is a band (the engine counts anything within ±5 as on it).
  const bandPath = () => {
    if (!spec.metrics.includes("bpm")) return "";
    return curveRuns(spec, "bpm").map((all) => {
      const up = all.map((p) => `${x(p.t).toFixed(1)},${y("bpm", Math.min(METRICS.bpm.max, p.v + 5)).toFixed(1)}`);
      const down = [...all].reverse().map((p) => `${x(p.t).toFixed(1)},${y("bpm", Math.max(METRICS.bpm.min, p.v - 5)).toFixed(1)}`);
      return `M${up.join(" L")} L${down.join(" L")} Z`;
    }).join(" ");
  };
  const bounds = sectionBounds(spec.sections);
  const filled = bounds.length ? bounds[bounds.length - 1].end : L;

  const valueOf = (p: FlowPick, m: Metric) =>
    m === "energy" ? p.energyLevel : m === "bpm" ? p.bpm : p.danceability;

  const info = METRICS[active];
  const others = spec.metrics.filter((m) => m !== active);

  return (
    <div className="flow-canvas" ref={wrap}>
      <svg
        ref={svg}
        width={width}
        height={HEIGHT}
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        role="application"
        aria-label={`${info.label} curve: press to add an anchor, drag to move, double-click to remove`}
      >
        {/* Grid and axes */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={PAD.top} y2={PAD.top + plotH} className="fc-grid" />
            <text x={x(t)} y={HEIGHT - 6} className="fc-axis" textAnchor="middle">{clock(t + offset)}</text>
          </g>
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const v = info.min + f * (info.max - info.min);
          return (
            <g key={f}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(active, v)} y2={y(active, v)} className="fc-grid" />
              <text x={PAD.left - 5} y={y(active, v) + 3} className="fc-axis" textAnchor="end" style={{ fill: info.color }}>
                {info.format(v)}
              </text>
            </g>
          );
        })}

        {/* Press anywhere empty to add an anchor for the metric in front. */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={plotW}
          height={plotH}
          className="fc-hit"
          onPointerDown={(e) => {
            if (!spec.metrics.includes(active)) return;
            const { px, py } = local(e);
            const p = clampPoint(active, L, { t: tOf(px), v: vOf(active, py) });
            const { points: next, index } = addPoint(points, p);
            onCurve(active, next);
            startDrag(e, index, next);
          }}
        />

        {bandPath() && <path d={bandPath()} className="fc-band" />}

        {/* Sections: a border and a name at the top of each; the time they leave, shaded. */}
        {bounds.map((b, i) => (
          <g key={`sec-${i}`} className="fc-section">
            {i > 0 && <line x1={x(b.start)} x2={x(b.start)} y1={PAD.top} y2={PAD.top + plotH} />}
            <text x={x(b.start) + 5} y={PAD.top + 11}>{b.name}</text>
          </g>
        ))}
        {bounds.length > 0 && filled < L && (
          <g className="fc-unfilled">
            <rect x={x(filled)} y={PAD.top} width={Math.max(0, x(L) - x(filled))} height={plotH} />
            <line x1={x(filled)} x2={x(filled)} y1={PAD.top} y2={PAD.top + plotH} />
            <text x={x(filled) + 5} y={PAD.top + 11}>Holds the last section's pace</text>
          </g>
        )}

        {/* The engine's answer: each song's value for the metric in front. */}
        {shown.map((p, i) => {
          const v = valueOf(p, active);
          const start = p.start - offset;
          if (v === null || start > L) return null;
          const x1 = x(start);
          const x2 = x(Math.min(L, start + p.air));
          return (
            <g key={`${p.trackId}@${p.start}`} className="fc-pick">
              <rect x={x1} y={y(active, v) - 2} width={Math.max(1, x2 - x1 - 1)} height={4} rx={1}
                    className={i % 2 ? "odd" : ""} />
              <title>{`${p.title ?? "Untitled"} — ${p.artist ?? ""}\n${clock(p.start)} · ${info.label} ${info.format(v)}${p.match !== null ? ` · Match ${Math.round(p.match)}` : ""}`}</title>
            </g>
          );
        })}

        {/* The other curves, faint. */}
        {others.map((m) => (
          <path key={m} d={pathFor(m)} className="fc-curve faint" style={{ stroke: METRICS[m].color }} />
        ))}

        {/* The curve being edited, with its anchors. */}
        {spec.metrics.includes(active) && (
          <>
            <path d={pathFor(active)} className="fc-curve" style={{ stroke: info.color }} />
            {points.map((p, i) => (
              <circle
                key={i}
                cx={x(p.t)}
                cy={y(active, p.v)}
                r={hover === i ? 7 : 5.5}
                style={{ fill: "var(--bg-1)", stroke: info.color, opacity: p.t > filled + 0.5 ? 0.35 : 1 }}
                className="fc-anchor"
                onPointerDown={(e) => {
                  // A second press on the same anchor within 400 ms is a double-click: remove it.
                  // (The drag below captures the pointer, so the browser's own dblclick never
                  // reaches the anchor; it is detected here instead.)
                  const now = performance.now();
                  const before = lastPress.current;
                  if (before && before.index === i && now - before.at < 400) {
                    e.preventDefault();
                    e.stopPropagation();
                    lastPress.current = null;
                    setHover(null);
                    onCurve(active, removePoint(points, i));
                    return;
                  }
                  lastPress.current = { index: i, at: now };
                  startDrag(e, i, points);
                }}
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
              >
                <title>{`${clock(p.t)} · ${info.label} ${info.format(p.v)}${p.t > filled + 0.5 ? " · past the sections, so not used" : ""} (double-click to remove)`}</title>
              </circle>
            ))}
            {hover !== null && points[hover] && (
              <text x={x(points[hover].t)} y={y(active, points[hover].v) - 11} className="fc-label" textAnchor="middle">
                {info.format(points[hover].v)} @ {clock(points[hover].t)}
              </text>
            )}
          </>
        )}

        {playX !== null && (
          <g className="fc-playhead">
            <line x1={playX} x2={playX} y1={PAD.top} y2={PAD.top + plotH} />
            <polygon points={`${playX - 5},${PAD.top - 6} ${playX + 5},${PAD.top - 6} ${playX},${PAD.top}`} />
            {pastEnd && <text x={playX - 4} y={PAD.top + 10} textAnchor="end" className="fc-label">holding · {clock(position!)}</text>}
          </g>
        )}

        {/* The time strip seeks. */}
        <rect
          x={PAD.left}
          y={PAD.top + plotH}
          width={plotW}
          height={PAD.bottom}
          className="fc-seek"
          onClick={(e) => onSeek(Math.max(0, tOf(local(e).px)) + offset)}
        >
          <title>Click to play from here</title>
        </rect>
      </svg>
      {spec.metrics.includes(active) && !points.length && (
        <div className="fc-empty">No {info.label.toLowerCase()} curve: press on the graph to add the first anchor.</div>
      )}
      {!spec.metrics.includes(active) && (
        <div className="fc-empty">{info.label} is not steering this flow. Tick it above to draw its curve.</div>
      )}
    </div>
  );
}

/** The target a curve sets at a listening second (for the now-playing readout). */
export const targetNow = (spec: FlowSpec, m: Metric, t: number) => sectionValueAt(spec.curves[m], spec.sections, curveMoment(spec, t));
